/* eslint-disable @typescript-eslint/no-explicit-any -- library rows arrive camelCase or snake_case */
/** Library type and workspace-scope rules, apart from utils.ts so the React Native app can share them. */

export type LibraryTypeValue = 'master' | 'global' | 'client_owned' | 'merchant';

export function getLibraryType(lib: any): LibraryTypeValue | null {
  return (lib?.libraryType ?? lib?.library_type) as LibraryTypeValue | undefined ?? null;
}

export function isMasterLibrary(lib: any): boolean {
  const type = getLibraryType(lib);
  return type === 'master' || type === 'global';
}

export function isClientOwnedLibrary(lib: any): boolean {
  return getLibraryType(lib) === 'client_owned';
}

export function isMerchantLibrary(lib: any): boolean {
  return getLibraryType(lib) === 'merchant';
}

/** The workspace_id stored on the library row (camelCase or snake_case). */
export function getLibraryWorkspaceId(lib: any): string | null {
  return (lib?.workspaceId ?? lib?.workspace_id) ?? null;
}

/**
 * Client-owned library names are a live link to the owning workspace — the
 * row stores NULL and the name resolves from a `workspaces(name)` embed on
 * the query. Master / merchant libraries keep their own stored name.
 */
export function resolveLibraryName<T extends Record<string, any>>(row: T): Omit<T, 'workspaces'> {
  const { workspaces, ...rest } = row as any;
  if (isClientOwnedLibrary(rest) && workspaces?.name) {
    return { ...rest, name: workspaces.name };
  }
  return rest;
}

/**
 * The workspace context the items pane should fetch for, given a library.
 * Client-owned libraries are always scoped to their own workspace; master /
 * merchant libraries fall back to the current workspace so per-workspace
 * overrides resolve correctly.
 */
export function effectiveWorkspaceId(lib: any, currentWorkspaceId: string | null | undefined): string | null {
  if (isClientOwnedLibrary(lib)) {
    return getLibraryWorkspaceId(lib) ?? currentWorkspaceId ?? null;
  }
  return currentWorkspaceId ?? null;
}
